/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Enterprise is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Enterprise is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Enterprise, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Enterprise:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Follows a 'code splitting' architecture.
 *  - Is completely custom built (no JS libraries other than instant.page)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Enterprise broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
 *  4.  on:line-item:change
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - product: the product object (includes a list of all variants)
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart. To listen for this, see the
 * on:line-item:change event.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the variant was added
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:line-item:change
 * -------------------------------------------------------------------------------------------------
 * Fires when the quantity of an item already in the cart is updated. Note, if the 'newQuantity' is
 * 0, this indicates the item was removed from that cart.
 *
 * Note, when adding a variant to the cart - this event will fire if that variant is already in the
 * cart (i.e. the quantity is incremented). In this situation, 'on:cart:add' will not fire.
 *
 * How to listen:
 * document.addEventListener('on:line-item:change', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the quantity change was completed
 *   - variantId: id of the variant that was just updated
 *   - newQuantity: new quantity of the line item
 *   - oldQuantity: old quantity of the line item
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes. See the 'Media Queries' section in this file
 * for more.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Enterprise utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 * =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Enterprise only has one third-party dependency: instant.page (https://instant.page/).
 *
 * It's included locally and is only active if it has been enabled in
 * 'Theme Settings > Advanced > Preload links on hover'.
 *
 * Instant.page is a JavaScript library that speeds up page loads by preloading links as soon as the
 * customer hovers over them.
 *
 *
 *
 * =================================================================================================
 * Code Splitting
 * =================================================================================================
 * We followed the ‘code splitting’ technique when building Enterprise.
 *
 * Code splitting consists in writing JavaScript (and CSS)in a modularized way within typically
 * small, more manageable files that can be loaded on-demand, as needed. The idea is to improve the
 * performance of our theme by reducing the amount of code that needs to be loaded upfront.
 *
 * If the customer is visiting a specific page of the theme that requires certain JavaScript
 * functionality, only the code needed for that page will be loaded, rather than one large
 * JavaScript file containing largely unused code. For example, the file media-gallery.js will
 * only be loaded if there is a media gallery on the page.
 *
 * Shopify uses HTTP/2, which is the newer version of the HTTP protocol used to deliver web content.
 * HTTP/2 supports multiplexing, which means that multiple requests can be sent over a single
 * connection at the same time - meaning multiple JS files are essentially served at the speed of a
 * single file.
 *
 * The only JS file which is served on every page in Enterprise is 'main.js'. Main.js contains
 * utility JS which is likely to be needed by many scripts. This is outlined more in the next
 * section.
 *
 *
 *
 * =================================================================================================
 * Utilities
 * =================================================================================================
 * Enterprise provides a few utility utilities, contained in main.js. Some of the key ones are
 * outlined below. See main.js for more.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 1) Lazy Loading
 * -------------------------------------------------------------------------------------------------
 * Lazy loading is a technique for delaying the loading of certain elements until they are needed,
 * which can help improve page load times.
 *
 * We use three functions used for lazy loading images and scripts in our theme:
 *
 *  - setImageSources function - copies the data-src and data-srcset attributes of lazy loaded
 *    images to their src and srcset attributes.
 *  - initLazyImages function - uses the IntersectionObserver API to lazy load images when needed.
 *  - initLazyScript function - only loads a script when a specific element is scrolled into view.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) Cookies
 * -------------------------------------------------------------------------------------------------
 * Cookies are small pieces of data that can be stored on a user's computer. They can be useful for
 * tracking user activity, remembering user preferences or other similar purposes.
 *
 * We use three functions to manage cookies:
 *
 *  - setCookie function - sets a cookie with a given name, value and number of days until it
 *  should expire.
 *  - getCookie function - takes in the name of a cookie to retrieve its value.
 *  - deleteCookie function - takes in the name of a cookie to delete it.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) Media Queries
 * -------------------------------------------------------------------------------------------------
 * The theme creates a theme.mediaMatches object (in theme.liquid) for several key screen sizes
 * specified in our theme, and adds listeners for each media query.
 *
 * These are:
 *
 * mediaQueries: {
 *  sm: '(min-width: 600px)',
 *  md: '(min-width: 769px)',
 *  lg: '(min-width: 1024px)',
 *  xl: '(min-width: 1280px)',
 *  xxl: '(min-width: 1536px)',
 *  portrait: '(orientation: portrait)'
 * }
 *
 * If a breakpoint is crossed, the mediaMatches values are updated and an 'on:breakpoint-change'
 * event is dispatched.
 *
 * You can request the entire theme.mediaMatches object to check which media queries are currently
 * matched. In this case, the returned data will be an object with the names of the media queries as
 * keys, and boolean values indicating whether they are currently matched or not.
 *
 * Example:
 *
 * {
 *  sm: true,
 *  md: true,
 *  lg: true,
 *  xl: true,
 *  xxl: false,
 *  portrait: false
 * }
 *
 * You can reference a specific media query to check if it's currently matched by using:
 * theme.mediaMatches.lg.
 *
 * To check if you're on mobile you can use:
 * !theme.mediaMatches.md
 *
 * If you want to perform some action when the breakpoint changes, you can listen for the
 * breakpoint-change event on the window object.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  // your code here
 * });
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */


// Constants
const TAG_TO_CHECK = 'BOGOF';
/*
function removeItemsWithVariant(variantId) {
  // Function to delete one instance of the item, then refetch and repeat if needed
  function deleteNextInstance() {
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        const itemToDelete = cart.items.find(item => item.id === variantId);

        if (itemToDelete) {
          // Find the line number for the current item with variantId
          const lineNumber = cart.items.indexOf(itemToDelete) + 1;

          // Delete this instance of the item
          fetch('/cart/change.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({
              line: lineNumber,
              quantity: 0
            })
          })
          .then(() => {
            // Wait 1 second, then try deleting the next instance
        deleteNextInstance();
          })
          .catch(error => console.error('Error:', error));
        } else {
          // Dispatch event to refresh the cart drawer once all items are deleted
          document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
            bubbles: true
          }));
        }
      })
      .catch(error => console.error('Error:', error));
  }

  // Start the deletion process
  deleteNextInstance();
}
*/


function removeItemsWithVariant(variantId) {
  const spinnerIcon = document.getElementById('spinner-icon');

  // Show spinner when function starts
  spinnerIcon.style.display = 'block';

  function deleteNextInstance() {
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        const itemToDelete = cart.items.find(item => item.id === variantId);

        if (itemToDelete) {
          // Find the line number for the current item with variantId
          const lineNumber = cart.items.indexOf(itemToDelete) + 1;

          // Delete this instance of the item
          fetch('/cart/change.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({
              line: lineNumber,
              quantity: 0
            })
          })
          .then(() => {
            // Recursively call to delete the next instance
            deleteNextInstance();
          })
          .catch(error => console.error('Error:', error));
        } else {
          // Hide the spinner once all items are deleted
          spinnerIcon.style.display = 'none';

          // Dispatch event to refresh the cart drawer
          document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
            bubbles: true
          }));
        }
      })
      .catch(error => console.error('Error:', error));
  }

  // Start the deletion process
  deleteNextInstance();
}




// Remove item from cart
function removeFromCart(variantId, cnt) {
  const data = {
 
       'quantity': cnt.toString(),
    'id': variantId.toString(),
  };

  // Define the URL to send the POST request to
  const url = '/cart/change.js';

  // Define options for the fetch request
  const options = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(data)
  };

  // Send the POST request
  return fetch(url, options)
    .then(response => {
 
document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
bubbles: true
}));

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      return response.json(); // Parse JSON response

      
    })
    .catch(error => {
      console.error('There was a problem with the fetch operation:', error);
    });
 
}

// BOGOF qty parity — update only the BOGOF lines of a variant, by line key.
// Final-chance lines (data-fc-line) share the variant but are a separate offer,
// so they are explicitly excluded — their quantity is independent of the BOGOF
// pair. Using line keys (not the variant id) guarantees the FC line is untouched.
function updateBogofLines(variantId, qty) {
    var rows = document.querySelectorAll('.cart-item[data-variant-id="' + variantId + '"]:not([data-fc-line])');
    var updates = {};
    rows.forEach(function(row) {
        var key = row.getAttribute('data-line-key');
        if (key) updates[key] = qty;
        var input = row.querySelector('input[type="number"]');
        if (input) input.value = qty;
    });
    return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: updates })
    })
    .then(function(response) {
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
        if (!response.ok) throw new Error('Network response was not ok');
        return response.json();
    })
    .catch(function(error) {
        console.error('There was a problem updating BOGOF lines:', error);
    });
}

// FC line stepper — hard-capped at data-max (3), updated by line key so it never
// touches the BOGOF lines. Clicking + past the cap is ignored and a "max reached"
// message is shown under the line.
function updateFcLine(lineKey, qty) {
    var updates = {};
    updates[lineKey] = qty;
    return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: updates })
    })
    .then(function(response) {
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
        if (!response.ok) throw new Error('Network response was not ok');
        return response.json();
    })
    .catch(function(error) {
        console.error('There was a problem updating the FC line:', error);
    });
}

document.addEventListener('click', function(event) {
    var btn = event.target.closest('[data-fc-qty]');
    if (!btn) return;
    event.preventDefault();
    var stepper = btn.closest('[data-fc-stepper]');
    if (!stepper) return;
    var max = parseInt(stepper.getAttribute('data-max') || '3', 10);
    var lineKey = stepper.getAttribute('data-line-key');
    var input = stepper.querySelector('input[type="number"]');
    var qty = parseInt(input.value, 10);
    if (!Number.isFinite(qty)) qty = 1;
    var cell = stepper.closest('td');
    var msg = cell ? cell.querySelector('[data-fc-max-msg]') : null;
    if (btn.getAttribute('data-fc-qty') === 'plus') {
        if (qty >= max) { if (msg) msg.style.display = 'block'; return; }
        qty++;
    } else {
        qty = Math.max(1, qty - 1);
    }
    if (msg && qty < max) msg.style.display = 'none';
    input.value = String(qty);
    updateFcLine(lineKey, qty);
});

document.addEventListener('DOMContentLoaded', function() {
    // Event listener for the plus button
    document.addEventListener('click', function(event) {
        if (event.target.classList.contains('qty-input__btn_bogofplus')) {
            // Get the quantity value
            var qtyInput = event.target.previousElementSibling;
            var qty = parseInt(qtyInput.value);
            // Increment the quantity value
            qty++;
            // Update only the BOGOF lines of this variant (FC lines excluded).
            var variantId = event.target.closest('.cart-item').getAttribute('data-variant-id');
            updateBogofLines(variantId, qty);
        }
    }); 

    // Event listener for the minus button
    document.addEventListener('click', function(event) {
        if (event.target.classList.contains('qty-input__btn_bogofminus')) {
            // Get the quantity value
            var qtyInput = event.target.nextElementSibling;
            var qty = parseInt(qtyInput.value);
            // Decrement the quantity value, but ensure it doesn't go below zero
            qty = Math.max(0, qty - 1);
            // Update only the BOGOF lines of this variant (FC lines excluded).
            var variantId = event.target.closest('.cart-item').getAttribute('data-variant-id');
            updateBogofLines(variantId, qty);
        }
    });
});

document.addEventListener('easysearch_loaded', function() {
  const match = window.location.href.match('/pages/([A-Za-z0-9]+?)-ink-cartridges');
  const prefillCollection = (match && match[1]) ? match[1].trim().toLowerCase() : '';

  if (!prefillCollection) return;

  const widget = document.querySelector('.easysearch-widget');
  if (!widget) return;

  function tryPrefillAndHide() {
    const select = widget.querySelector('.easysearch-select-holder select');
    if (!select || !select.options || select.options.length === 0) return false;

    // Find matching option (case-insensitive, trimmed)
    let matched = false;
    for (const o of select.querySelectorAll('option')) {
      if (o.textContent.trim().toLowerCase() === prefillCollection) {
        select.value = o.value || o.textContent;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        matched = true;
        break;
      }
    }
    if (!matched) return false;

    // Hide the visible holder (and any UI clones) with inline !important
    const holder = select.closest('.easysearch-select-holder') || select.parentElement;
    if (holder && holder.style) holder.style.setProperty('display', 'none', 'important');

    // Common custom dropdown wrappers to also hide if present
    const clone = select.nextElementSibling;
    if (clone && (clone.classList.contains('select2') || clone.classList.contains('nice-select'))) {
      clone.style.setProperty('display', 'none', 'important');
    }

    return true;
  }

  // Attempt immediately; if options aren’t ready, observe briefly
  if (tryPrefillAndHide()) return;

  const observer = new MutationObserver(() => {
    if (tryPrefillAndHide()) {
      observer.disconnect();
    }
  });
  observer.observe(widget, { childList: true, subtree: true });

  // Safety: stop observing after a few seconds
  setTimeout(() => observer.disconnect(), 4000);
});
// EasySearch custom


